import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {Button, Modal, Space, Table} from 'antd';
import {CardProps} from 'antd/es/card';
import {ColumnProps} from 'antd/lib/table';
import {
  Acquirer,
  PaymentMethods,
  Processor,
  ProviderFieldsFragment,
  useDeleteProviderMutation,
  useProvidersQuery
} from 'api/generated';
import {providersQuery} from 'api/queries';
import {ButtonLink, TableCard} from 'components';
import {acquirerLabels, paymentMethodLabels, paymentProcessorLabels} from 'lib/labels';
import React from 'react';
import {Link, useHistory} from 'react-router-dom';
import {ProviderStatusTags} from './Provider/ProviderStatusTag';

export type Props = {
  accountId: string;
  cardProps: CardProps;
};

export const Providers: React.FC<Props> = ({accountId, cardProps}) => {
  const history = useHistory();
  const {data, loading} = useProvidersQuery({variables: {accountId}});
  const [deleteProvider] = useDeleteProviderMutation({
    updateQuery: {query: providersQuery, variables: {accountId}}
  });
  const providers = data?.providers || [];

  const handleDelete = (id: string) => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'This action will delete the provider.',
      okText: 'Confirm',
      okType: 'danger',
      onOk: () => deleteProvider({variables: {id}})
    });
  };

  const handleSelect = (provider: any) => ({
    onClick: (e: React.MouseEvent) => {
      if (['View', 'Edit'].includes(e.target.outerText)) return;
      e.preventDefault();
      const url = `/accounts/${accountId}/providers/${provider.id}`;
      if (e.ctrlKey || e.metaKey) {
        window.open(url, provider.id)?.focus();
      } else {
        history.push(url);
      }
    }
  });

  const columns: Array<ColumnProps<ProviderFieldsFragment>> = [
    {
      title: 'Processor',
      dataIndex: 'processor',
      key: 'processor',
      render: (processor: string) => paymentProcessorLabels[processor as Processor],
      onCell: (provider) => handleSelect(provider)
    },
    {
      title: 'Payment method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (value: string) => paymentMethodLabels[value as PaymentMethods],
      onCell: (provider) => handleSelect(provider)
    },
    {
      title: 'Acquirer',
      dataIndex: 'acquirer',
      key: 'acquirer',
      render: (value: Acquirer) => acquirerLabels[value],
      onCell: (provider) => handleSelect(provider)
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      className: 'minimized-cell',
      onCell: (provider) => handleSelect(provider),
      render: (_: boolean, provider: ProviderFieldsFragment) => (
        <ProviderStatusTags provider={provider} />
      )
    },
    {
      title: 'Actions',
      className: 'minimized-cell',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, provider) => (
        <Space>
          <ButtonLink size="small" to={`/accounts/${accountId}/providers/${provider.id}`}>
            <EyeOutlined /> View
          </ButtonLink>
          <ButtonLink size="small" to={`/accounts/${accountId}/providers/${provider.id}/edit`}>
            <EditOutlined /> Edit
          </ButtonLink>
          <Button
            size="small"
            danger
            onClick={() => handleDelete(provider.id)}
            disabled={Boolean(provider.enabled)}>
            <DeleteOutlined />
          </Button>
        </Space>
      )
    }
  ];

  return (
    <TableCard
      {...cardProps}
      title="Payment methods"
      extra={<Link to={`/accounts/${accountId}/providers/new`}>Add payment method</Link>}>
      <Table
        columns={columns}
        rowKey="id"
        dataSource={providers}
        pagination={false}
        loading={loading}
        rowClassName={() => 'clickable'}
      />
    </TableCard>
  );
};
